import React from 'react';
import {
  Users,
  ExternalLink,
  Heart,
  Calendar,
  Megaphone,
  HandHeart,
  ArrowRight,
  Globe
} from 'lucide-react';

const TAGPTA = () => {
  const externalUrl = 'https://www.tagscholarspta.org/';

  const ptaBenefits = [
    {
      icon: Heart,
      title: 'Community Connection',
      description: 'Connect with other TAG families and build lasting relationships'
    },
    {
      icon: Megaphone,
      title: 'Stay Informed',
      description: 'Get the latest updates on school events, programs, and initiatives'
    },
    {
      icon: Calendar,
      title: 'Participate in Events',
      description: 'Join exciting school events, fundraisers, and family activities'
    },
    {
      icon: HandHeart,
      title: 'Support Our School',
      description: 'Help enhance educational opportunities for all TAG students'
    }
  ];

  return (
    <main id="main-content" className="bg-white pt-20">
      {/* Hero Section */}
      <section className="section-padding hero-gradient text-white">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 mb-8">
              <Users className="h-5 w-5" />
              <span className="text-sm font-medium tracking-wide uppercase">Community Partnership</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              <span className="block font-extralight text-white/90">TAG</span>
              <span className="block font-medium">PTA</span>
            </h1>

            <p className="text-xl text-white/90 mb-12 font-light max-w-3xl mx-auto leading-relaxed">
              Join our vibrant parent-teacher community working together to support our students and enhance the TAG experience.
            </p>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-8">
              <Globe className="h-12 w-12 mx-auto mb-4 text-gold-400" />
              <p className="text-white/90 mb-6 font-light">
                The TAG PTA runs its own website with membership info, events, and volunteer sign-ups.
              </p>
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-primary-900 hover:bg-white/95 font-medium px-10 py-4 rounded-lg transition-all duration-300 shadow-lg"
              >
                Go to TAG PTA Website Now <ExternalLink className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join the PTA Section */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold-100 text-gold-700 px-4 py-2 rounded-full mb-6">
              <Heart className="h-4 w-4" />
              <span className="text-sm font-medium">Get Involved</span>
            </div>

            <h2 className="text-4xl font-light text-primary-900 mb-6 leading-tight">
              Why Join the
              <span className="font-medium block">TAG PTA?</span>
            </h2>

            <p className="text-lg text-primary-700 font-light leading-relaxed">
              The TAG PTA plays a vital role in enriching our school community. When you join the PTA,
              you become part of a network of dedicated families working to make TAG even better.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {ptaBenefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-neutral-50 rounded-2xl p-8 border border-neutral-200 card-hover"
              >
                <div className="w-14 h-14 bg-gold-50 rounded-lg flex items-center justify-center mb-6 border border-gold-100">
                  <benefit.icon className="h-7 w-7 text-gold-700" />
                </div>
                <h3 className="text-xl font-medium text-primary-900 mb-3">{benefit.title}</h3>
                <p className="text-primary-700 font-light leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What the PTA Does Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
                <Megaphone className="h-4 w-4" />
                <span className="text-sm font-medium">Our Mission</span>
              </div>

              <h2 className="text-4xl font-light text-primary-900 mb-6 leading-tight">
                What the
                <span className="font-medium block">PTA Does</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-xl p-6 text-center border border-neutral-200 shadow-sm">
                <Calendar className="h-10 w-10 text-primary-700 mx-auto mb-4" />
                <h3 className="font-medium text-primary-900 mb-2">Organize Events</h3>
                <p className="text-sm text-primary-700 font-light">
                  Plan and host family events, fundraisers, and celebrations
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center border border-neutral-200 shadow-sm">
                <HandHeart className="h-10 w-10 text-primary-700 mx-auto mb-4" />
                <h3 className="font-medium text-primary-900 mb-2">Support Programs</h3>
                <p className="text-sm text-primary-700 font-light">
                  Fund enrichment programs and educational resources
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center border border-neutral-200 shadow-sm">
                <Users className="h-10 w-10 text-primary-700 mx-auto mb-4" />
                <h3 className="font-medium text-primary-900 mb-2">Build Community</h3>
                <p className="text-sm text-primary-700 font-light">
                  Foster connections between families, teachers, and staff
                </p>
              </div>
            </div>

            <div className="bg-primary-700 rounded-2xl p-12 text-white text-center">
              <h2 className="text-3xl font-light mb-6">Ready to Get Involved?</h2>
              <p className="text-xl text-white mb-8 font-light max-w-2xl mx-auto">
                Visit the TAG PTA website to learn more about membership, upcoming events, and how you can contribute to our school community.
              </p>
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-primary-900 hover:bg-white/95 font-medium px-10 py-4 rounded-lg transition-all duration-300 shadow-lg"
              >
                Visit TAG PTA Website <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-padding text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-light text-primary-900 mb-8">Have Questions?</h2>
            <p className="text-lg text-primary-700 mb-8 font-light">
              For more information about the TAG PTA, visit our website or reach out to our PTA leadership team.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center"
              >
                TAG PTA Website <ExternalLink className="ml-2 h-4 w-4" />
              </a>
              <a href="/resources/parents" className="btn-secondary">
                Parent Resources
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TAGPTA;
